import { userHasPermissionToViewDeviceDetails } from '@order-management/roles';
import {
  DeviceRegistrationStatus,
  RegistrationFileDevicesCriteria,
  RegistrationFileDevicesExpandableTableRow,
  RegistrationFileDevicesTableRow,
} from '@order-management/types';
import { useCallback, useMemo } from 'react';
import Conditional from '../../shared/conditional/conditional';
import useUser from '../../shared/hooks/user';
import { formatDateToLocaleString } from '../../shared/utils';
import Link from '../../ui/link/link';
import PaginatedTable, {
  CustomPaginatedTableProps,
} from '../../ui/table/paginated-table/paginated-table';
import {
  Column,
  ExpandableRow,
  OnTableChangeFunction,
} from '../../ui/table/types';

export type RegistrationFileDevicesTableProps =
  CustomPaginatedTableProps<RegistrationFileDevicesTableRow> & {
    onChange: OnTableChangeFunction<
      RegistrationFileDevicesTableRow,
      RegistrationFileDevicesExpandableTableRow
    >;
    filterCriteria: RegistrationFileDevicesCriteria;
  };

export function RegistrationFileDevicesTable({
  dataSource,
  config,
  onChange,
  empty,
  form,
  filterCriteria,
}: RegistrationFileDevicesTableProps) {
  const { user } = useUser();
  const isExpandableRow = useCallback(
    (row: RegistrationFileDevicesTableRow) => {
      return row.registrations.length > 1;
    },
    []
  );

  const expandableRow = useMemo(():
    | ExpandableRow<
        RegistrationFileDevicesTableRow,
        RegistrationFileDevicesExpandableTableRow
      >
    | undefined => {
    return {
      expandable: (row: RegistrationFileDevicesTableRow) =>
        isExpandableRow(row),
      expandableRowIterator: (row: RegistrationFileDevicesTableRow) => {
        return row.registrations;
      },
    };
  }, [isExpandableRow]);

  const columns: Column<
    RegistrationFileDevicesTableRow,
    RegistrationFileDevicesExpandableTableRow
  >[] = [
    {
      key: 'serialNumber.number',
      className: 'is-width-15',
      render: (el) => (
        <Conditional
          condition={userHasPermissionToViewDeviceDetails(
            user,
            el.salesOrder.id
          )}
        >
          <Conditional.True>
            <Link to={`/devices/${el.id}`}>{el.serialNumber}</Link>
          </Conditional.True>
          <Conditional.False>
            <div>{el.serialNumber}</div>
          </Conditional.False>
        </Conditional>
      ),
      title: 'Serial number',
      sortable: true,
    },
    {
      key: 'name',
      className: 'is-width-15',
      render: (el) => el.name,
      title: 'Device name',
      sortable: true,
    },
    {
      key: 'model.displayName',
      className: 'is-width-10',
      render: (el) => el.type,
      title: 'Device type',
      sortable: true,
    },
    {
      key: 'status',
      className: 'is-width-10',
      render: (el) => {
        let status: string = el.status;
        if (
          status === DeviceRegistrationStatus.UNREGISTERED &&
          el.registrations.length > 0 &&
          filterCriteria.relatedRegisteredDevices
        ) {
          status = status + '*';
        }
        return status;
      },
      title: 'Status',
      sortable: false,
    },
    {
      key: 'registrations.registrationMethod',
      className: 'is-width-35',
      render: (el) => {
        if (isExpandableRow(el)) {
          return null;
        }
        if (
          el.status === DeviceRegistrationStatus.UNREGISTERED &&
          el.registrations.length === 0
        ) {
          return '-';
        }
        return <span>{el.registrations[0].registrationMethod}</span>;
      },
      expandableContentRender: (
        el: RegistrationFileDevicesExpandableTableRow
      ) => <span>{el.registrationMethod}</span>,
      title: 'Registration method',
      sortable: false,
    },
    {
      key: 'registrations.registrationDate',
      className: 'is-width-10',
      render: (el) => {
        if (isExpandableRow(el)) {
          return null;
        }
        if (
          el.status === DeviceRegistrationStatus.UNREGISTERED &&
          el.registrations.length === 0
        ) {
          return '-';
        }
        return (
          <span>
            {el.registrations[0].registrationDate
              ? formatDateToLocaleString(el.registrations[0].registrationDate)
              : '-'}
          </span>
        );
      },
      expandableContentRender: (
        el: RegistrationFileDevicesExpandableTableRow
      ) => (
        <span>
          {el.registrationDate
            ? formatDateToLocaleString(el.registrationDate)
            : '-'}
        </span>
      ),
      title: 'Registration date',
      sortable: false,
    },
  ];

  return (
    <PaginatedTable
      dataSource={dataSource}
      columns={columns}
      config={config}
      rowKey={'id'}
      onChange={onChange}
      empty={empty}
      form={form}
      expandableRow={expandableRow}
    />
  );
}

export default RegistrationFileDevicesTable;
